import _ from 'lodash';
import axios from 'axios';
import Errors from './Errors';

class Form {
  /**
   * Create a new Form instance.
   *
   * @param {object} data
   */
  constructor(data) {
    if (Array.isArray(data)) {
      data = data.reduce((carry, element) => {
        carry[element] = '';
        return carry;
      }, {});
    }

    this.originalData = data;
    this.errors = new Errors();
    this.processing = false;
    this.resetAfterSuccess = true;

    _.each(data, (value, field) => {
      this[field] = data[field];
    });
  }

  dontResetAfterSuccess() {
    this.resetAfterSuccess = false;
    return this;
  }

  withFiles() {
    this.enableFileUpload = true;
    return this;
  }

  /**
   * Fetch all relevant data for the form.
   */
  data() {
    if (this.enableFileUpload) {
      // workaround for bug - php returns empty for HTTP PUT/PATCH requests encoded as multipart/form-data
      // in other words, can't upload files with PUT/PATCH requests using axios
      for (const key in this.originalData) {
        this.originalData[key] = this[key];
      }
      const formData = this.objectToFormData(this.originalData);
      formData.append('_method', this.requestType);
      this.requestType = 'post';
      return formData;
    }
    const data = {};
    _.each(this.originalData, (value, property) => {
      data[property] = this[property];
    });
    return data;
  }
  // if this not working well, try
  // https://gist.github.com/ghinda/8442a57f22099bdb2e34 or
  // https://github.com/therealparmesh/object-to-formdata/blob/master/index.js
  buildFormData(formData, data, parentKey) {
    if (
      data &&
      typeof data === 'object' &&
      !(data instanceof Date) &&
      !(data instanceof File)
    ) {
      Object.keys(data).forEach(key => {
        this.buildFormData(
          formData,
          data[key],
          parentKey ? `${parentKey}[${key}]` : key,
        );
      });
    } else {
      const value = data === null ? '' : data;
      formData.append(parentKey, value);
    }
  }

  objectToFormData(data) {
    // use formData to enable file upload
    const formData = new FormData();
    this.buildFormData(formData, data);
    return formData;
  }

  /**
   * Assigning model data to form data
   * @param {object} data
   * @param {array} exceptions
   */
  assign(data, exceptions) {
    if (!data) {
      return this;
    }
    if (exceptions) {
      exceptions = Array.isArray(exceptions) ? exceptions : [exceptions];
    }
    for (const key in data) {
      if (typeof this[key] !== 'undefined') {
        if (exceptions && exceptions.indexOf(key) !== -1) {
          continue;
        }
        this.originalData[key] = data[key];
        this[key] = data[key];
      }
    }
    return this;
  }

  /**
   * Reset the form fields.
   */
  reset() {
    _.each(this.originalData, (value, field) => {
      this[field] = this.originalData[field];
    });

    this.errors.clear(null);
  }

  /**
   * Clear the form fields.
   */
  clear() {
    _.each(this.originalData, (value, field) => {
      this[field] = '';
    });

    this.errors.clear(null);
  }

  /**
   * Send a POST request to the given URL.
   *
   * @param {string} url
   */
  post(url) {
    this.requestType = 'post';
    return this.submit(url);
  }

  /**
   * Send a PUT request to the given URL.
   *
   * @param {string} url
   */
  put(url) {
    this.requestType = 'put';
    return this.submit(url);
  }

  /**
   * Send a PATCH request to the given URL.
   *
   * @param {string} url
   */
  patch(url) {
    this.requestType = 'patch';
    return this.submit(url);
  }

  /**
   * Send a DELETE request to the given URL.
   *
   * @param {string} url
   */
  delete(url) {
    this.requestType = 'delete';
    return this.submit(url);
  }

  /**
   * Submit the form.
   *
   * @param {string} requestType
   * @param {string} url
   */
  submit(url) {
    this.validateRequestType(this.requestType);
    this.errors.clear(null);
    this.processing = true;

    const data = this.data();
    return new Promise((resolve, reject) => {
      axios[this.requestType](url, data)
        .then(response => {
          this.processing = false;
          this.onSuccess();
          resolve(response.data);
        })
        .catch(error => {
          this.processing = false;
          this.onFail(error.response.data.errors);

          reject(error.response);
        });
    });
  }

  /**
   * Validate a request type.
   *
   * @param {string} requestType
   */
  validateRequestType(requestType) {
    const requestTypes = ['get', 'delete', 'head', 'post', 'put', 'patch'];

    if (requestTypes.indexOf(requestType) === -1) {
      throw new Error(
        `${requestType} is not a valid request type, ` +
          `must be one of: ${requestTypes.join('`, `')}.`,
      );
    }
  }

  /**
   * Handle a successful form submission.
   *
   * @param {object} data
   */
  onSuccess() {
    if (this.resetAfterSuccess) {
      this.reset();
    }
  }

  /**
   * Handle a failed form submission.
   *
   * @param {object} errors
   */
  onFail(errors) {
    this.errors.record(errors);
  }
}

export default Form;
